import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ResultDisplay.module.css';

function extractCropPolygon(cropRaw) {
  if (!cropRaw || typeof cropRaw !== 'object') return null;

  const crops = cropRaw.crops;
  if (!Array.isArray(crops) || crops.length === 0) return null;

  const crop = crops[0];
  const location = crop.location;
  const poly = location?.polygon ?? crop.polygon ?? crop.boundingBox ?? crop.bounding_box;
  if (!Array.isArray(poly) || poly.length < 3) return null;

  const points = [];
  for (const pt of poly) {
    if (Array.isArray(pt) && pt.length === 2 && typeof pt[0] === 'number' && typeof pt[1] === 'number') {
      points.push([pt[0], pt[1]]);
    } else if (pt && typeof pt === 'object' && !Array.isArray(pt)) {
      if (typeof pt.x === 'number' && typeof pt.y === 'number') {
        points.push([pt.x, pt.y]);
      }
    }
  }

  if (points.length >= 3 && points.every(([x, y]) => x >= 0 && x <= 1 && y >= 0 && y <= 1)) {
    return points;
  }
  return null;
}

function drawCropPolygonOnCanvas(canvas, img, polygon) {
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const w = img.naturalWidth;
  const h = img.naturalHeight;

  ctx.drawImage(img, 0, 0);

  ctx.beginPath();
  ctx.moveTo(polygon[0][0] * w, polygon[0][1] * h);
  for (let j = 1; j < polygon.length; j++) {
    ctx.lineTo(polygon[j][0] * w, polygon[j][1] * h);
  }
  ctx.closePath();

  ctx.strokeStyle = '#00FF00';
  ctx.lineWidth = Math.max(3, Math.round(w / 200));
  ctx.stroke();

  ctx.fillStyle = 'rgba(0, 255, 0, 0.15)';
  ctx.fill();

  const fontSize = Math.max(14, Math.round(w / 40));
  ctx.font = `bold ${fontSize}px sans-serif`;
  const labelX = polygon[0][0] * w;
  const labelY = polygon[0][1] * h - 6;
  const text = 'Crop Boundary';
  const textWidth = ctx.measureText(text).width;
  ctx.fillStyle = '#00FF00';
  ctx.fillRect(labelX, labelY - fontSize - 2, textWidth + 10, fontSize + 6);
  ctx.fillStyle = '#000';
  ctx.fillText(text, labelX + 5, labelY - 2);

  ctx.fillStyle = '#FF3B30';
  for (const [x, y] of polygon) {
    const r = Math.max(4, Math.round(w / 150));
    ctx.beginPath();
    ctx.arc(x * w, y * h, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function CardSideSection({ label, imageBlob, rawImageBlob, insuranceData, mode }) {
  const [imageUrl, setImageUrl] = useState('');
  const canvasRef = useRef(null);
  const [showPolygon, setShowPolygon] = useState(true);
  const [hasCropPolygon, setHasCropPolygon] = useState(false);

  useEffect(() => {
    if (insuranceData?.croppedImage) {
      setImageUrl(`data:image/jpeg;base64,${insuranceData.croppedImage}`);
      return;
    }
    const url = URL.createObjectURL(imageBlob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageBlob, insuranceData]);

  const drawOverlay = useCallback(() => {
    if (!showPolygon) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const polygon = insuranceData?.cropRaw ? extractCropPolygon(insuranceData.cropRaw) : null;
    setHasCropPolygon(polygon != null);

    const sourceBlob = rawImageBlob || imageBlob;
    const url = URL.createObjectURL(sourceBlob);
    const img = new Image();
    img.onload = () => {
      if (polygon) {
        drawCropPolygonOnCanvas(canvas, img, polygon);
      } else {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.drawImage(img, 0, 0);
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [insuranceData, showPolygon, rawImageBlob, imageBlob]);

  useEffect(() => {
    drawOverlay();
  }, [drawOverlay]);

  const hasCropData = mode === 'insurance' && insuranceData?.cropRaw != null;

  return (
    <div className={styles.card}>
      {label && <div className={styles.sideLabel}>{label}</div>}

      {hasCropData && (
        <button
          onClick={() => setShowPolygon((v) => !v)}
          style={{
            background: showPolygon ? '#007AFF' : '#6b7280',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '6px 12px',
            fontSize: 13,
            cursor: 'pointer',
            margin: '8px 12px 0',
            alignSelf: 'flex-start',
          }}
        >
          {showPolygon ? `Hide Crop Polygon${hasCropPolygon ? '' : ' (none found)'}` : 'Show Crop Polygon'}
        </button>
      )}

      {showPolygon && hasCropData ? (
        <canvas ref={canvasRef} className={styles.cardImage} style={{ width: '100%', height: 'auto' }} />
      ) : (
        <img
          src={imageUrl}
          alt="Scanned card"
          className={styles.cardImage}
          style={
            !insuranceData?.croppedImage && insuranceData?.rotationAngle
              ? { transform: `rotate(${-insuranceData.rotationAngle}deg)` }
              : undefined
          }
        />
      )}

      {mode === 'insurance' && insuranceData && (
        <div className={styles.fields}>
          <Field label="Insurance Name" value={insuranceData.insuranceName} />
          <Field label="Policy Number" value={insuranceData.policyNumber} />
          <Field label="Group Number" value={insuranceData.groupNumber} />
          <Field label="Member Name" value={insuranceData.memberName} />
        </div>
      )}

      {mode === 'id' && (
        <div className={styles.fields}>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>ID card captured successfully.</p>
        </div>
      )}
    </div>
  );
}

export function ResultDisplay({
  mode,
  imageBlob,
  rawImageBlob,
  insuranceData,
  backImageBlob,
  backInsuranceData,
  ocrError,
  onScanAnother,
  onDone,
}) {
  const hasBothSides = !!backImageBlob;

  return (
    <div className={styles.container}>
      <CardSideSection
        label={hasBothSides ? 'Front' : undefined}
        imageBlob={imageBlob}
        rawImageBlob={rawImageBlob}
        insuranceData={insuranceData}
        mode={mode}
      />

      {backImageBlob && (
        <CardSideSection label="Back" imageBlob={backImageBlob} insuranceData={backInsuranceData ?? null} mode={mode} />
      )}

      {ocrError && <div className={styles.errorBanner}>{ocrError}</div>}

      <div className={styles.actions}>
        <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onScanAnother}>
          Scan another
        </button>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {value ? <span className={styles.fieldValue}>{value}</span> : <span className={styles.fieldEmpty}>Not detected</span>}
    </div>
  );
}
